import { TableRow, TableCell, Chip, CircularProgress, styled, Menu, MenuItem, Tooltip, IconButton } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { IProforma } from "../../../interfaces/IProforma";
import EditIcon from "@mui/icons-material/Edit";
import InvoicesController from "../../../Api/invoicesController";
import { useToast } from "../../../hooks/useToast";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PositionsTable from "./components/PositionsTable";


const statusOrder = [
    "awaiting_adv",
    "adv_paid",
    "sent",
    "received",
    "cancelled"
] as const;

const statusLabels: Record<string, string> = {
    awaiting_adv: "Oczekuje na zaliczkę",
    adv_paid: "Zaliczka opłacona",
    sent: "Wysłane",
    received: "Odebrane",
    cancelled: "Anulowane"
};

const statusColors: Record<string, "warning" | "info" | "primary" | "success" | "error" | "default"> = {
    awaiting_adv: "warning",
    adv_paid: "info",
    sent: "primary",
    received: "success",
    cancelled: "error"
};

const StyledInput = styled('input')(({ theme }) => ({
    width: 80,
    padding: "6px 8px",
    border: `1.5px solid ${theme.palette.grey[300]}`,
    borderRadius: 6,
    fontSize: 15,
    background: "#fafbfc",
    color: theme.palette.text.primary,
    outline: "none",
    transition: "border-color 0.18s, box-shadow 0.18s",
    boxShadow: "0 1px 4px 0 rgba(60,60,60,0.04)",
    "&:focus": {
        borderColor: theme.palette.primary.main,
        background: "#fff",
        boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
    },
}));


interface InvoiceItemProps {
    setInvoicesData: React.Dispatch<React.SetStateAction<IProforma[] | null>>;
    invoice: IProforma;
}


const formatInvoiceNumber = (number: number, date: Date) => {
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${number}/${month}/${year}`;
};

const InvoiceItem: FC<InvoiceItemProps> = ({
    setInvoicesData,
    invoice
}) => {
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
    const [statusMenuInvoiceId, setStatusMenuInvoiceId] = useState<string | null>(null);
    const [zk, setZk] = useState<string>(invoice.zk || '');
    const [zd, setZd] = useState<string>(invoice.zd || '');
    const [zkLoading, setZkLoading] = useState<boolean>(false);
    const [zdLoading, setZdLoading] = useState<boolean>(false);
    const [statusLoading, setStatusLoading] = useState<boolean>(false);
    const [expandDetails, setExpandDetails] = useState<boolean>(false);
    const zkTimeout = useRef<NodeJS.Timeout | null>(null);
    const zdTimeout = useRef<NodeJS.Timeout | null>(null);
    const toast = useRef(useToast()).current;
    const navigate = useNavigate();


    const handleZkChange = async (value: string) => {
        let firstValue = invoice.zk
        setZk(value);
        setZkLoading(true);
        if (zkTimeout.current) clearTimeout(zkTimeout.current);
        zkTimeout.current = setTimeout(async () => {
            const res = await InvoicesController.editProformaZk({ _id: invoice._id, zk: value });
            if (!res.ok) {
                setZkLoading(false);
                toast.error(res.message);
                setZk(firstValue);
                return;
            }
            setZkLoading(false);
        }, 1500);
    };


    const handleZdChange = (value: string) => {
        let firstValue = invoice.zd
        setZd(value);
        setZdLoading(true);
        if (zdTimeout.current) clearTimeout(zdTimeout.current);
        zdTimeout.current = setTimeout(async () => {
            const res = await InvoicesController.editProformaZd({ _id: invoice._id, zd: value });
            if (!res.ok) {
                setZdLoading(false);
                toast.error(res.message);
                setZd(firstValue);
                return;
            }
            setZdLoading(false);
        }, 1500);
    };


    const handleStatusSelect = async (status: IProforma["status"]) => {
        setStatusLoading(true);
        if (statusMenuInvoiceId) {
            setInvoicesData(prev => prev?.map(inv =>
                inv._id === statusMenuInvoiceId ? { ...inv, status } : inv
            ) ?? null);

            const res = await InvoicesController.editProformaStatus({
                _id: statusMenuInvoiceId,
                status,
            });
            if (!res.ok) {
                setInvoicesData(prev => prev?.map(inv =>
                    inv._id === statusMenuInvoiceId ? { ...inv, status: inv.status } : inv
                ) ?? null);
                toast.error(res.message);
            }
        }
        setStatusMenuAnchor(null);
        setStatusMenuInvoiceId(null);
        setStatusLoading(false);
    };

    const handleStatusLeftClick = async (invoice: IProforma) => {
        setStatusLoading(true);
        const idx = statusOrder.indexOf(invoice.status as typeof statusOrder[number]);
        if (idx !== -1 && idx < statusOrder.length - 2) {
            const nextStatus = statusOrder[idx + 1] as IProforma["status"];
            setInvoicesData(prev => prev?.map(inv =>
                inv._id === invoice._id ? { ...inv, status: nextStatus } : inv
            ) ?? null);

            const res = await InvoicesController.editProformaStatus({
                _id: invoice._id,
                status: nextStatus,
            });
            if (!res.ok) {
                setInvoicesData(prev => prev?.map(inv =>
                    inv._id === invoice._id ? { ...inv, status: invoice.status } : inv
                ) ?? null);
                toast.error(res.message);
            }
        }
        setStatusLoading(false);
    };


    const handleStatusRightClick = (event: React.MouseEvent, invoice: IProforma) => {
        event.preventDefault();
        setStatusMenuAnchor(event.currentTarget as HTMLElement);
        setStatusMenuInvoiceId(invoice._id);
    };


    useEffect(() => {
        return () => {
            if (zkTimeout.current) clearTimeout(zkTimeout.current);
            if (zdTimeout.current) clearTimeout(zdTimeout.current);
        };
    }, []);


    return (
        <>
            <>
                <TableRow key={invoice._id} hover>
                    <TableCell align="left">
                        {formatInvoiceNumber(invoice.number, invoice.date)}
                    </TableCell>
                    <TableCell align="left">
                        {new Date(invoice.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: 500 }}>
                        {invoice.clientName}
                    </TableCell>
                    <TableCell align="left">
                        {invoice.total?.toLocaleString("pl-PL", { minimumFractionDigits: 0 }) ?? "-"} PLN
                    </TableCell>
                    <TableCell align="left">
                        {invoice.advanceAmmount?.toLocaleString("pl-PL", { minimumFractionDigits: 0 }) ?? "-"} PLN
                    </TableCell>
                    <TableCell align="center">
                        <span
                            onClick={() => handleStatusLeftClick(invoice)}
                            onContextMenu={e => handleStatusRightClick(e, invoice)}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            <Chip
                                label={statusLabels[invoice.status]}
                                color={statusColors[invoice.status] || "default"}
                                size="small"
                                sx={{ ml: `${statusLoading ? '0' : '22px'}` }}
                            />
                            {statusLoading && <CircularProgress size={18} sx={{ ml: '4px' }} />}
                        </span>
                    </TableCell>
                    <TableCell>
                        {zkLoading && <CircularProgress size={18} sx={{ mr: '4px' }} />}
                        <StyledInput
                            value={zk}
                            onChange={e => handleZkChange(e.target.value)}
                            sx={{ ml: `${zkLoading ? '0' : '22px'}` }}
                        />
                    </TableCell>
                    <TableCell>
                        {zdLoading && <CircularProgress size={18} sx={{ mr: '4px' }} />}
                        <StyledInput
                            value={zd}
                            onChange={e => handleZdChange(e.target.value)}
                            sx={{ ml: `${zdLoading ? '0' : '22px'}` }}
                        />
                    </TableCell>
                    <TableCell align="left">
                        <Tooltip title="Edytuj" onClick={() => navigate(`/proforma/${invoice._id}`)}>
                            <IconButton color="secondary" size="small">
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Szczegóły" onClick={() => setExpandDetails(!expandDetails)}>
                            <IconButton sx={{ ml: 2 }} color='inherit' size="small">
                                {expandDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </Tooltip>
                    </TableCell>
                </TableRow>
                <Menu
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={() => setStatusMenuAnchor(null)}
                >
                    {statusOrder.map(status => (
                        <MenuItem
                            key={status}
                            selected={
                                invoice?._id === statusMenuInvoiceId && invoice?.status === status
                            }
                            onClick={() => handleStatusSelect(status)}
                        >
                            {statusLabels[status]}
                        </MenuItem>
                    ))}
                </Menu>
            </>
            <PositionsTable
                positions={invoice.positions}
                invoiceId={invoice._id}
                expand={expandDetails}
            />
        </>
    )
}

export default InvoiceItem;